import React, { useState } from 'react';
import { ChevronDown, HelpCircle, MapPin } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  category?: string;
}

const FAQS: FAQItem[] = [
  {
    question: 'How does Blishka doorstep parlour service work?',
    answer:
      'Booking home beauty care with Blishka is quick and seamless. Browse our online price menu or packages, pick your desired services, select a date and convenient time slot, and confirm. Our background-verified female beauty expert arrives at your home with sterilized tools, disposable kits, and salon-grade cosmetics. You enjoy professional parlour care right in your living room.'
  },
  {
    question: 'Which areas do you serve?',
    answer:
      'We currently serve Power House, Sector 1 to Sector 10, Kohka, Pragati Nagar, Risali, Vaishali Nagar, and Supela with free doorstep parlour care (min ₹300 order). Other areas are coming soon!'
  },
  {
    question: 'Are the beauticians verified and is the service safe for women?',
    answer:
      '100% safe. Blishka is an exclusive doorstep salon service for women. Every beauty professional on our team is an experienced, certified, and thoroughly background-verified female specialist. You can relax in complete comfort and privacy.'
  },
  {
    question: 'What hygiene and sanitization standards are followed?',
    answer:
      'We follow strict single-use hygiene protocols. Every appointment includes disposable bedsheets, single-use waxing strips, disposable towels, sanitized stainless steel tools, and sealed product sachets. Tools are disinfected with alcohol wipes before each session.'
  },
  {
    question: 'What is the minimum booking amount for a home visit?',
    answer:
      'Individual services start from as low as ₹30 (e.g., upper lip threading). The minimum cart order for a free doorstep visit is just ₹300 across Bhilai, with absolutely zero travel fees, equipment charges, or hidden taxes.'
  },
  {
    question: 'Which brands and products are used for waxing and facials?',
    answer:
      'We use only genuine, salon-grade, skin-tested branded cosmetics. For waxing, we offer classic Honey Wax, White Chocolate Wax, and premium Italian Rica Liposoluble Wax. For facials and cleanups, we use VLCC, Lotus Herbals, Aroma Magic, and O3+ Bridal Glow kits, alongside L\'Oreal Professional hair care.'
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'Payment is made only after your service is completed to your 100% satisfaction. We accept all major UPI options (Google Pay, PhonePe, Paytm, BHIM) as well as Cash.'
  },
  {
    question: 'Can I reschedule or cancel my appointment?',
    answer:
      'Rescheduling and cancellation is available only 1 hour before appointment starts.'
  }
];

export const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleAccordion = (idx: number) => {
    setOpenIndex(prev => (prev === idx ? null : idx));
  };

  return (
    <section id="faq" className="faq-section" aria-labelledby="faq-main-heading">
      <div className="section-container">
        {/* Section Header */}
        <div className="section-title-wrap">
          <div className="section-eyebrow-chip">
            <HelpCircle size={14} color="var(--blishka-berry)" />
            <span>Got Questions?</span>
          </div>
          <h2 id="faq-main-heading" className="section-main-heading">
            Frequently Asked Questions
          </h2>
          <span className="section-script-subtitle">
            Everything you need to know about doorstep beauty in Bhilai ♡
          </span>
        </div>

        {/* FAQ Accordion List */}
        <div className="faq-accordion-list" role="region" aria-label="FAQ Accordion">
          {FAQS.map((faq, idx) => {
            const isOpen = openIndex === idx;
            return (
              <div
                key={idx}
                className={`faq-accordion-item ${isOpen ? 'active' : ''}`}
              >
                <button
                  type="button"
                  className="faq-question-btn"
                  onClick={() => toggleAccordion(idx)}
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${idx}`}
                  id={`faq-btn-${idx}`}
                >
                  <span className="faq-question-text">{faq.question}</span>
                  <div className={`faq-icon-arrow ${isOpen ? 'rotate' : ''}`}>
                    <ChevronDown size={18} />
                  </div>
                </button>

                <div
                  id={`faq-answer-${idx}`}
                  role="region"
                  aria-labelledby={`faq-btn-${idx}`}
                  className={`faq-answer-collapse ${isOpen ? 'show' : ''}`}
                >
                  <div className="faq-answer-inner">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Local SEO Neighborhood Badges */}
        <div className="local-seo-area-box">
          <div className="local-seo-header">
            <MapPin size={16} color="var(--blishka-berry)" />
            <h4>Serving Available Neighborhoods Across Bhilai</h4>
          </div>
          <p className="local-seo-desc">
            Free doorstep parlour visit (min ₹300) available 7 days a week (9:00 AM – 7:30 PM):
          </p>
          <div className="local-seo-tags-grid">
            <span className="local-seo-tag">Power House</span>
            <span className="local-seo-tag">Sector 1 to 10</span>
            <span className="local-seo-tag">Kohka</span>
            <span className="local-seo-tag">Pragati Nagar</span>
            <span className="local-seo-tag">Risali</span>
            <span className="local-seo-tag">Vaishali Nagar</span>
            <span className="local-seo-tag">Supela</span>
          </div>
        </div>
      </div>
    </section>
  );
};
